import {
	siteCloneInFlight,
	siteClonePhase,
	type ChatSiteClone,
} from "./messages.js";

export function siteCloneNeedsPoll(clone: ChatSiteClone): boolean {
	if (clone.publishPhase === "publishing") return true;
	if (
		clone.publishDecision === "approved" &&
		clone.status !== "published" &&
		clone.publishPhase !== "deployed"
	) {
		return true;
	}
	return siteCloneInFlight(clone);
}

export function patchFromSiteStatus(
	site: Record<string, unknown>,
	prev: ChatSiteClone,
): Partial<ChatSiteClone> {
	const status = String(site.status ?? prev.status);
	const publishedUrl =
		typeof site.publishedUrl === "string" && site.publishedUrl.trim()
			? site.publishedUrl
			: null;
	const published = status === "published" || Boolean(publishedUrl);
	const publishing = status === "publishing" && !published;
	const lastError =
		typeof site.lastError === "string" && site.lastError.trim()
			? site.lastError
			: null;
	const failed =
		!published &&
		!publishing &&
		Boolean(lastError) &&
		(prev.publishDecision === "approved" ||
			prev.publishPhase === "publishing" ||
			prev.publishPhase === "failed");
	return {
		status,
		progress:
			typeof site.progress === "number" ? site.progress : prev.progress,
		phase: published
			? "ready"
			: status === "preview_ready"
				? "ready"
				: status === "failed"
					? "failed"
					: siteClonePhase(site.phase),
		previewUrl:
			typeof site.previewUrl === "string" ? site.previewUrl : prev.previewUrl,
		publishedUrl: publishedUrl ?? prev.publishedUrl,
		repoUrl: typeof site.repoUrl === "string" ? site.repoUrl : prev.repoUrl,
		publishPhase: published
			? "deployed"
			: publishing
				? "publishing"
				: failed
					? "failed"
					: prev.publishPhase,
		publishDecision: published ? "approved" : prev.publishDecision,
		lastError: published ? lastError : (lastError ?? prev.lastError),
		name: typeof site.name === "string" ? site.name : prev.name,
		plannedPageCount: readCount(site, "plannedPageCount") ?? prev.plannedPageCount,
		pagesCompleted: readCount(site, "pagesCompleted") ?? prev.pagesCompleted,
		etaRemainingSec: readCount(site, "etaRemainingSec") ?? prev.etaRemainingSec,
	};
}

function readCount(
	site: Record<string, unknown>,
	key: "plannedPageCount" | "pagesCompleted" | "etaRemainingSec",
): number | null | undefined {
	const direct = site[key];
	if (typeof direct === "number" && Number.isFinite(direct)) return direct;
	const meta = site.meta;
	if (meta && typeof meta === "object" && !Array.isArray(meta)) {
		const nested = (meta as Record<string, unknown>)[key];
		if (typeof nested === "number" && Number.isFinite(nested)) return nested;
	}
	return undefined;
}

/** “24 pages · About 8 minutes” — only after the runner has a sitemap plan. */
export function formatClonePlan(clone: {
	plannedPageCount?: number | null;
	etaRemainingSec?: number | null;
}): string | null {
	const pages = clone.plannedPageCount;
	const wait = formatCloneWait(clone.etaRemainingSec);
	const pageBit =
		pages != null && pages > 0
			? `${pages} page${pages === 1 ? "" : "s"}`
			: null;
	if (pageBit && wait) return `${pageBit} · ${wait}`;
	return wait ?? pageBit;
}

export function formatCloneWait(sec: number | null | undefined): string | null {
	if (sec == null || !Number.isFinite(sec) || sec <= 0) return null;
	if (sec < 90) return "About a minute";
	const minutes = Math.round(sec / 60);
	if (minutes === 1) return "About a minute";
	if (minutes < 60) return `About ${minutes} minutes`;
	return "About an hour";
}
